// src/components/SurveyRenderer.jsx
//
// Schema-driven renderer for the Personnel / HR Survey (and any future
// survey that fits the same shape). The schema (from survey_definitions)
// drives section + question order, labels, hints, and follow-up logic.
// Response values are read from / written to a flat state object whose
// keys map 1:1 to typed columns on deal_personnel_survey.
//
// Supported question types:
//   - text          : single-line input
//   - textarea      : multi-line input
//   - number        : numeric input
//   - date          : <input type="date" />
//   - yes_no        : two-option radio + clear button
//   - yes_no_unsure : three-option radio
//   - yes_no_maybe  : three-option radio
//   - select        : <select> with `options: [{ value, label }]`
//   - multi_bool    : array of related boolean fields (e.g. employee mix)
//   - multi_select  : checkbox group bound to a text[] column
//
// Yes/No-style fields are stored as booleans in the DB but exposed to
// the form state as 'yes' / 'no' / '' so radios cleanly express the
// three-state semantics (yes / no / unset).

import React from 'react';

const inputCls = 'w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-300';
const labelCls = 'block text-xs text-gray-500 mb-0.5';

function YesNoRadio({ field, value, onChange, options = ['yes','no'] }) {
  return (
    <div className="flex gap-3 mt-1 items-center flex-wrap">
      {options.map(opt => (
        <label key={opt} className="inline-flex items-center gap-1 text-sm cursor-pointer">
          <input
            type="radio"
            name={field}
            checked={value === opt}
            onChange={() => onChange(opt)}
          />
          <span className="capitalize">{opt.replace(/_/g, ' ')}</span>
        </label>
      ))}
      {value !== '' && value != null && (
        <button type="button" onClick={() => onChange('')}
                className="text-xs text-gray-400 hover:text-gray-600 underline">clear</button>
      )}
    </div>
  );
}

function QuestionInput({ question, state, onPatch }) {
  const v = state[question.id] ?? '';
  const set = (next) => onPatch({ [question.id]: next });

  switch (question.type) {
    case 'text':
      return <input className={inputCls} value={v} onChange={e => set(e.target.value)} />;
    case 'textarea':
      return <textarea className={inputCls} rows={2} value={v} onChange={e => set(e.target.value)} />;
    case 'number':
      return <input type="number" className={inputCls + ' max-w-[240px]'} value={v} onChange={e => set(e.target.value)} />;
    case 'date':
      return <input type="date" className={inputCls + ' max-w-[220px]'} value={v || ''} onChange={e => set(e.target.value)} />;
    case 'yes_no':
      return <YesNoRadio field={question.id} value={v} onChange={set} options={['yes','no']} />;
    case 'yes_no_unsure':
      return <YesNoRadio field={question.id} value={v} onChange={set} options={['yes','no','unsure']} />;
    case 'yes_no_maybe':
      return <YesNoRadio field={question.id} value={v} onChange={set} options={['yes','no','maybe']} />;
    case 'select':
      return (
        <select className={inputCls + ' max-w-[280px]'} value={v} onChange={e => set(e.target.value)}>
          <option value="">—</option>
          {(question.options || []).map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      );
    case 'multi_bool':
      return (
        <div className="flex flex-wrap gap-3 mt-1">
          {(question.fields || []).map(f => (
            <label key={f.id} className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={!!state[f.id]}
                onChange={e => onPatch({ [f.id]: e.target.checked })}
              />
              {f.label}
            </label>
          ))}
        </div>
      );
    case 'multi_select': {
      const arr = Array.isArray(v) ? v : [];
      const toggle = (val) => {
        const s = new Set(arr);
        if (s.has(val)) s.delete(val); else s.add(val);
        set(Array.from(s));
      };
      return (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mt-1">
          {(question.options || []).map(opt => (
            <label key={opt.value} className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
              <input type="checkbox" checked={arr.includes(opt.value)} onChange={() => toggle(opt.value)} />
              {opt.label}
            </label>
          ))}
        </div>
      );
    }
    default:
      return <input className={inputCls} value={v} onChange={e => set(e.target.value)} />;
  }
}

function shouldShowFollowUp(q, state) {
  if (!q.follow_up) return false;
  const cur = state[q.id];
  return cur === q.follow_up.when;
}

function FollowUp({ question, state, onPatch }) {
  const fu = question.follow_up;
  if (!fu) return null;
  if (!shouldShowFollowUp(question, state)) return null;
  // Render a synthetic question for the follow-up
  const followQuestion = {
    id: fu.field,
    type: fu.type || 'textarea',
    options: fu.options,
    fields: fu.fields,
  };
  return (
    <div className="mt-2">
      {fu.label && <label className={labelCls}>{fu.label}</label>}
      <QuestionInput question={followQuestion} state={state} onPatch={onPatch} />
    </div>
  );
}

// ============================================================================
// Public renderer — interactive form for the seller portal.
// ============================================================================

export function SurveyForm({ schema, state, onPatch }) {
  if (!schema?.sections) return null;
  const sections = [...schema.sections]
    .filter(s => !s.hidden)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div>
      {schema.description && <p className="text-xs text-gray-500 mb-2">{schema.description}</p>}
      {sections.map(section => {
        const questions = [...(section.questions || [])]
          .filter(q => !q.hidden)
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        if (questions.length === 0) return null;
        return (
          <div key={section.id}>
            <h4 className="text-xs font-semibold uppercase tracking-wider mt-5 mb-2 pb-1 border-b" style={{ color: '#475569' }}>
              {section.label}
            </h4>
            {questions.map(q => (
              <div key={q.id} className="mb-3">
                <label className={labelCls}>{q.label}{q.required && <span style={{ color: '#DC2626' }}> *</span>}</label>
                <QuestionInput question={q} state={state} onPatch={onPatch} />
                {q.hint && <p className="text-[11px] text-gray-400 mt-0.5">{q.hint}</p>}
                <FollowUp question={q} state={state} onPatch={onPatch} />
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Read-only renderer — used in DealView for internal review.
// Only renders fields that actually have a value.
// ============================================================================

function formatValue(question, value, allState) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) {
    const opts = question.options || [];
    return value.map(v => opts.find(o => o.value === v)?.label || v).join(', ') || null;
  }
  if (question.type === 'select') {
    const opt = (question.options || []).find(o => o.value === value);
    return opt?.label || value;
  }
  if (question.type === 'yes_no' || question.type === 'yes_no_unsure' || question.type === 'yes_no_maybe') {
    return String(value).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
  return value;
}

export function SurveyReview({ schema, data }) {
  if (!schema?.sections || !data) return null;
  const sections = [...schema.sections]
    .filter(s => !s.hidden)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="space-y-3">
      {sections.map(section => {
        const questions = [...(section.questions || [])]
          .filter(q => !q.hidden)
          .sort((a, b) => (a.order || 0) - (b.order || 0));

        const renderable = [];
        for (const q of questions) {
          if (q.type === 'multi_bool') {
            const present = (q.fields || []).filter(f => data[f.id]).map(f => f.label);
            if (present.length) renderable.push({ label: q.label, value: present.join(', ') });
          } else {
            const formatted = formatValue(q, data[q.id], data);
            if (formatted) renderable.push({ label: q.label, value: formatted });
            if (q.follow_up) {
              const showFollow = data[q.id] === q.follow_up.when;
              if (showFollow) {
                const fuQuestion = { id: q.follow_up.field, type: q.follow_up.type, options: q.follow_up.options };
                const formattedFu = formatValue(fuQuestion, data[q.follow_up.field], data);
                if (formattedFu) renderable.push({ label: q.follow_up.label || q.follow_up.field, value: formattedFu });
              }
            }
          }
        }
        if (!renderable.length) return null;
        return (
          <div key={section.id}>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">{section.label}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {renderable.map(({ label, value }) => (
                <div key={label} className="bg-gray-50 rounded p-1.5">
                  <div className="text-gray-400 text-[11px]">{label}</div>
                  <div className="font-medium text-xs whitespace-pre-wrap">{value}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// State serialization helpers — convert between form state ('yes'/'no'/'')
// and DB shape (true/false/null) so the schema-driven form plays nicely
// with the typed columns on deal_personnel_survey.
// ============================================================================

function collectFieldIds(schema) {
  const ids = new Set();
  for (const section of schema?.sections || []) {
    for (const q of section.questions || []) {
      ids.add(q.id);
      if (q.type === 'multi_bool') {
        for (const f of q.fields || []) ids.add(f.id);
      }
      if (q.follow_up) ids.add(q.follow_up.field);
    }
  }
  return Array.from(ids);
}

const YN_TYPES = new Set(['yes_no','yes_no_unsure','yes_no_maybe']);

function questionTypeFor(schema, id) {
  for (const section of schema?.sections || []) {
    for (const q of section.questions || []) {
      if (q.id === id) return q.type;
      if (q.type === 'multi_bool' && (q.fields || []).some(f => f.id === id)) return 'bool';
      if (q.follow_up?.field === id) return q.follow_up.type || 'textarea';
    }
  }
  return null;
}

// DB → form state. Booleans on yes/no fields become 'yes'/'no'/''.
export function dbToFormState(schema, dbRow) {
  const out = {};
  const ids = collectFieldIds(schema);
  for (const id of ids) {
    const t = questionTypeFor(schema, id);
    const v = dbRow?.[id];
    if (YN_TYPES.has(t)) {
      out[id] = v === true ? 'yes' : v === false ? 'no' : (typeof v === 'string' ? v : '');
    } else if (t === 'bool') {
      out[id] = !!v;
    } else if (t === 'multi_select') {
      out[id] = Array.isArray(v) ? v : [];
    } else {
      out[id] = v ?? '';
    }
  }
  return out;
}

// Form state → submit_seller_info payload.
// Only pure yes_no fields map to boolean DB columns ('yes'→true, 'no'→false, ''→null).
// yes_no_unsure / yes_no_maybe fields map to text DB columns with CHECK constraints
// ('yes','no','unsure' / 'yes','no','maybe') so their string values must pass through
// unchanged — converting them to booleans would violate the CHECK constraint.
export function formStateToDb(schema, state) {
  const out = {};
  const ids = collectFieldIds(schema);
  for (const id of ids) {
    const t = questionTypeFor(schema, id);
    const v = state[id];
    if (t === 'yes_no') {
      out[id] = v === 'yes' ? true : v === 'no' ? false : null;
    } else {
      out[id] = v;
    }
  }
  return out;
}
